import React, { useCallback, useEffect, useRef, useState } from 'react';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import { useForm } from 'react-hook-form';
import {
  PropertyToAmenities,
  PropertyToCharacteristics,
  UpdatePropertyAmenitiesApiPayload,
  UpdatePropertyAmenitiesInfoForm,
} from '../models/propertyModel';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  CreateAmenityPayload,
  CreateFeatureCharacteristicPayload,
  createAmenityEndpoint,
  createFeatureCharacteristicEndpoint,
  getAmenitiesEndpoint,
  getCharacteristicsEndpoint,
  updatePropertyEndpoint,
} from '../services/propertyService';
import { useToast } from '../../../components/toast/ToastProvider';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import {
  buildCharacteristics,
  buildFeatureCharacteristics,
  getCurrentAmenities,
  getCurrentFeatureIds,
} from '../adapters/propertyAdapter';

const useAdminAmenities = (
  property_id: string,
  amenities_info: PropertyToAmenities[],
  property_characteristics: PropertyToCharacteristics[] = [],
) => {
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  // Características extra que la propiedad tiene guardadas. El backend solo hace upsert, así que
  // lo que se desmarca se manda con measure 0: la lista tiene que reflejar lo ya guardado, incluso
  // tras guardar varias veces sin recargar la página (al guardar se le suma lo recién enviado).
  const previousFeatureIds = useRef<string[]>(getCurrentFeatureIds(property_characteristics));
  useEffect(() => {
    previousFeatureIds.current = getCurrentFeatureIds(property_characteristics);
  }, [property_characteristics]);
  const toast = useToast();
  const [characteristicsCatalog, setCharacteristicsCatalog] = useState<any[]>([]);
  const [amenitiesCatalog, setAmenitiesCatalog] = useState<any[]>([]);
  const [currentAmenities, setCurrentAmenities] = useState<any[]>([]);

  const loadCatalogs = useCallback(async () => {
    const characteristicResponse = await callEndpoint(
      getCharacteristicsEndpoint(token),
    );
    const amenitiesResponse = await callEndpoint(
      getAmenitiesEndpoint(token)
    );

    const characteristics = fetchResponseAdapter(
      characteristicResponse.data
    );
    const amenities = fetchResponseAdapter(
      amenitiesResponse.data
    );
    setCharacteristicsCatalog(
      characteristics.data
    );
    // Opciones del multiselect: además de value/label llevan el ícono de Tabler
    // para mostrarlo en la lista.
    const amenityOptions = amenities.data.map(
      ({ id, description, icon }: { id: string; description: string; icon: string }) => ({
        value: id,
        label: description,
        icon,
      }),
    );
    setAmenitiesCatalog(amenityOptions);
    setCurrentAmenities(getCurrentAmenities(amenityOptions, amenities_info));

  }, [amenities_info, callEndpoint, token]);

  // Agrega la característica al catálogo y la deja disponible en el multiselect.
  // Devuelve la creada (o la que ya existía con ese nombre) para seleccionarla.
  const createFeatureCharacteristic = useCallback(
    async (payload: CreateFeatureCharacteristicPayload) => {
      const response = await callEndpoint(
        createFeatureCharacteristicEndpoint(payload, token),
      );
      if (response.status !== 200 && response.status !== 201) return null;
      const { data: created } = fetchResponseAdapter(response.data);
      setCharacteristicsCatalog((catalog) =>
        catalog.some(({ id }) => id === created.id) ? catalog : [...catalog, created],
      );
      return created;
    },
    [callEndpoint, token],
  );

  // Agrega la amenidad al catálogo y la deja disponible en el multiselect.
  // Devuelve la opción creada para seleccionarla.
  const createAmenity = useCallback(
    async (payload: CreateAmenityPayload) => {
      const response = await callEndpoint(
        createAmenityEndpoint(payload, token),
      );
      if (response.status !== 200 && response.status !== 201) {
        toast.error('No se pudo crear la amenidad.');
        return null;
      }
      const { data: created } = fetchResponseAdapter(response.data);
      const option = {
        value: created.id,
        label: created.description,
        icon: created.icon,
      };
      setAmenitiesCatalog((catalog) => [...catalog, option]);
      return option;
    },
    [callEndpoint, token, toast],
  );

  const updatePropertyAmenities = useCallback(async (formInfo: UpdatePropertyAmenitiesInfoForm) => {

    // Transform form info into payload
    const payload: UpdatePropertyAmenitiesApiPayload = {
      has_white_goods: formInfo.has_white_goods,
      is_furnished: formInfo.is_furnished,
      pets_allowed: formInfo.pets_allowed,
      characteristics_info: [
        ...buildCharacteristics(
          characteristicsCatalog,
          formInfo.c_rooms,
          formInfo.c_bathrooms,
          formInfo.c_half_bathrooms,
          formInfo.c_parkings,
          formInfo.c_square_mts,
        ),
        ...buildFeatureCharacteristics(
          characteristicsCatalog,
          formInfo.features_info ?? [],
          previousFeatureIds.current,
        ),
      ],
      amenities_info: formInfo.amenities_info,
      advantages: formInfo.advantages ?? [],
    };

    const { status } = await callEndpoint(
      updatePropertyEndpoint(payload, property_id, token),
    );
    if (status === 200) {
      // Se queda en esta pantalla: el formulario ya tiene lo guardado.
      toast.success('Amenidades y características actualizadas.');
      previousFeatureIds.current = Array.from(
        new Set([...previousFeatureIds.current, ...(formInfo.features_info ?? [])]),
      );
    } else {
      toast.error('No se pudo guardar. Intenta de nuevo.');
    }

  }, [callEndpoint, toast, characteristicsCatalog, property_id, token]);

  useEffect(() => {
    loadCatalogs().catch(e => console.error(e));
  }, []);



  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    register,
    reset,
  } = useForm<UpdatePropertyAmenitiesInfoForm>({
    resolver: yupResolver(
      yup
        .object({
          has_white_goods: yup.boolean(),
          is_furnished: yup.boolean(),
          pets_allowed: yup.boolean(),
        })
        .required(),
    ),
  });

  return {
    // Form
    control,
    register,
    errors,
    reset,
    handleSubmit,
    setValue,

    // Request
    isLoading,

    // Catalogs
    amenitiesCatalog,
    currentAmenities,
    characteristicsCatalog,

    // Update funcs
    updatePropertyAmenities,
    createFeatureCharacteristic,
    createAmenity,
  };
};

export default useAdminAmenities;
